/**
 * What a session's `scaled_score` actually means.
 *
 * The column is shared across exam families — AP stores 1-5 in it, ACT stores a 1-36 composite —
 * so nothing about the number alone says which. The scoring profile carries the answer in
 * `metadata.scale`; this turns that into a label and a maximum for the ring.
 *
 * Profiles with no `scale` are AP, which is every profile that predates ACT — so the default
 * keeps existing tests rendering exactly as before.
 */
// short — for compact chips, where "ACT Composite" won't fit.
// min/max — the valid range for a manual override input. Clamping an ACT composite to AP's 1-5
// makes the override field unusable, so the bounds have to come from the scale too.
// step — the granularity of a manual override input. AMC moves in halves (+1.5 per blank), so an
// integer-only input could not enter a real AMC score.
const SCALES = {
    ap_1_5: { label: "AP Score", short: "AP", min: 1, max: 5, step: 1 },
    act_1_36: { label: "ACT Composite", short: "ACT", min: 1, max: 36, step: 1 },
    sat_400_1600: { label: "SAT Total", short: "SAT", min: 400, max: 1600, step: 10 },
    // Points per answer (6 / 1.5 / 0 over 25 questions), no curve — see the server's
    // answerPointsScoring.helper.js. The session also stores the correct/blank/wrong breakdown.
    // outOf — print the score as "102/150" on the grading tables. AP's 1-5 and ACT's 1-36 are read as
    // bare numbers; an AMC total is a sum of points, so it reads naturally against its max.
    amc_0_150: { label: "AMC Score", short: "AMC", min: 0, max: 150, step: 0.5, outOf: true },
};

const DEFAULT_SCALE = SCALES.ap_1_5;

// For payloads that carry the scale key flat (the test-results dashboard sends `scale`).
export const scaleFromKey = (key) => (key && SCALES[key]) || DEFAULT_SCALE;

// test — a v2_tests payload carrying v2_scoring_profile.metadata.scale.
export const scaleOf = (test) => scaleFromKey(test?.v2_scoring_profile?.metadata?.scale);

export const scaleLabel = (test) => scaleOf(test).label;
export const scaleMax = (test) => scaleOf(test).max;

// How a stored scaled score reads in a table cell: "4" for AP, "30" for ACT, "102/150" for AMC.
// `max` lets a caller pass the session's own max (6 × its question count) instead of the scale's.
export const formatScaled = (scale, value, max = null) => {
    if (value == null) return null;
    const sc = typeof scale === "string" ? scaleFromKey(scale) : (scale || DEFAULT_SCALE);
    return sc.outOf ? `${value}/${max ?? sc.max}` : `${value}`;
};

export default SCALES;
